import { motion } from "framer-motion";
import { B, PILLARS } from "../data/pillars";
import { getRiskLevel, getUrgency } from "../data/recommendations";
import SectionHeader from "./SectionHeader";

export default function UrgencyMatrix({ answers, pillarScores, confidenceMap }) {
  const allItems = [];

  PILLARS.forEach(p => {
    p.questions.forEach(q => {
      const score = (answers[q.id] !== undefined ? answers[q.id] + 1 : 1);
      const urgency = getUrgency(score);
      const isUnsure = confidenceMap[q.id] === false;

      allItems.push({
        pillar: p.label,
        pillarColor: p.color,
        question: q.label,
        score,
        urgency,
        isUnsure,
        qid: q.id
      });
    });
  });

  const actNow = allItems.filter(i => i.urgency.label === "ACT NOW").sort((a, b) => a.score - b.score);
  const planQ2 = allItems.filter(i => i.urgency.label === "PLAN FOR Q2");
  const optimize = allItems.filter(i => i.urgency.label === "OPTIMIZE");

  const Group = ({ title, color, icon, items }) => (
    <div style={{ marginBottom: 20 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
        <span style={{ fontSize: 18 }}>{icon}</span>
        <span style={{ fontSize: 13, fontWeight: 800, color, letterSpacing: 1.5, textTransform: "uppercase" }}>{title}</span>
        <span style={{ background: color + "20", color, fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 10 }}>{items.length}</span>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {items.map((item, i) => (
          <motion.div
            key={item.qid}
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: i * 0.04 }}
            style={{
              background: "#122240", borderRadius: 8, padding: "10px 14px", border: `1px solid ${color}25`,
              display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap"
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10, flex: 1 }}>
              <div style={{ width: 8, height: 8, borderRadius: "50%", background: item.pillarColor, flexShrink: 0 }} />
              <div>
                <span style={{ color: B.white, fontSize: 13, fontWeight: 600 }}>{item.question}</span>
                <span style={{ color: B.subText, fontSize: 11, marginLeft: 8 }}>· {item.pillar}</span>
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexShrink: 0 }}>
              {item.isUnsure && (
                <span style={{ background: B.yellow + "20", color: B.yellow, fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 4, border: `1px solid ${B.yellow}40` }}>
                  ⚠ NEEDS VALIDATION
                </span>
              )}
              <span style={{ background: getRiskLevel(item.score).color + "20", color: getRiskLevel(item.score).color, fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 4 }}>
                OML {item.score}
              </span>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );

  return (
    <div style={{ background: "#1A2E4A", borderRadius: 16, border: "1px solid #1E3A5F", padding: "clamp(20px,4vw,32px)", marginBottom: 24, boxSizing: "border-box" }}>
      <SectionHeader num={3} title="Remediation Priority Matrix" sub="Action Plan — Ranked by Severity" />

      <p style={{ color: B.lightGray, fontSize: 14, lineHeight: 1.7, marginBottom: 24 }}>
        Items ranked by operational urgency to provide a clear sequence for remediation. <span style={{ color: B.yellow, fontWeight: 600 }}>⚠ Needs Validation</span> flags indicate areas where current state is unsure and require further technical validation.
      </p>

      {actNow.length > 0 && <Group title="Act Now — Critical Priority" color={B.red} icon="🚨" items={actNow} />}
      {planQ2.length > 0 && <Group title="Plan for Q2 — High Priority" color={B.orange} icon="📅" items={planQ2} />}
      {optimize.length > 0 && <Group title="Optimize — Roadmap Item" color={B.green} icon="✅" items={optimize} />}
    </div>
  );
}